import { Button } from '@startuptools/ui/joy/Button';
import { Controller } from 'react-hook-form';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Trans, useLingui } from '@lingui/react';
import { Switch } from '@startuptools/ui/joy/Switch';
import { Box } from '@startuptools/ui/joy/Box';
import { Stack } from '@startuptools/ui/joy/Stack';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import { FormLabel } from '@startuptools/ui/joy/FormLabel';
import { FormHelperText } from '@startuptools/ui/joy/FormHelperText';
import { SplitLabel } from '../../../share-register/transactions/components/SplitLabel';
import { GqlArticlesOfAssoc } from '../../../../graphql/base-types.graphql';
import { GqlAoaGetAffectedQuery, useAoaGetAffectedLazyQuery } from '../../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';
import { DateTime } from 'luxon';
import { StepProps } from '../../types';

type FormData = Pick<GqlArticlesOfAssoc, 'numberOfSharesAfter'>;

export const StepSplit = ({
  onSave,
  stepNext,
  onToggle,
  aoa,
  loading,
}: {
  onSave: StepProps['onSave'];
  stepNext: () => void;
  onToggle?: (createSplit: boolean) => Promise<unknown>;
  aoa:
    | Pick<GqlArticlesOfAssoc, 'numberOfSharesBefore' | 'numberOfSharesAfter' | 'id' | 'changeNumberOfShares'>
    | null
    | undefined;
  loading: boolean;
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const form = useForm<FormData>({
    defaultValues: { numberOfSharesAfter: aoa?.numberOfSharesAfter ?? aoa?.numberOfSharesBefore },
  });
  const [showSplit, setShowSplit] = React.useState(Boolean(aoa?.changeNumberOfShares));
  const [getAffected] = useAoaGetAffectedLazyQuery();

  const [affectedData, setAffectedData] = useState<GqlAoaGetAffectedQuery['aoaGetAffected'] | null>(null);

  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);
  return (
    <Stack gap={2}>
      <Box sx={{ mt: 2 }}>
        <Switch
          checked={showSplit}
          onChange={async event => {
            if (!event.target.checked) {
              form.setValue('numberOfSharesAfter', aoa?.numberOfSharesBefore);
            }
            setShowSplit(event.target.checked);
            await onToggle?.(event.target.checked);
          }}
          color={showSplit ? 'primary' : 'neutral'}
          variant={showSplit ? 'solid' : 'outlined'}
          endDecorator={<Trans id="Make a split/reverse split" />}
          slotProps={{
            endDecorator: {
              sx: {
                minWidth: 24,
              },
            },
          }}
        />
      </Box>

      {showSplit ? (
        <form
          onSubmit={form.handleSubmit(async formData => {
            if (!aoa) return;
            const res = await getAffected({ variables: { id: aoa?.id, companyId } });
            const affected = res.data?.aoaGetAffected;

            const shareIssues = affected?.shareIssues ?? [];
            const warrantPrograms = affected?.warrantPrograms ?? [];
            const authorizationGroups = affected?.authorizationGroups ?? [];

            if (shareIssues.length > 0 || warrantPrograms.length > 0 || authorizationGroups.length > 0) {
              setPendingFormData(formData);
              setAffectedData({ shareIssues, warrantPrograms, authorizationGroups });
            } else {
              await onSave(formData);
              stepNext();
            }
          })}
        >
          <Stack gap={2} maxWidth={800}>
            <FormControl>
              <FormLabel>
                <Trans id="Number of Shares Before" />
              </FormLabel>
              <FormHelperText>
                <Trans id="Filled automatically" />
              </FormHelperText>
              <NumericFormatInput
                sx={{ maxWidth: '200px' }}
                value={aoa?.numberOfSharesBefore ?? ''}
                disabled
                lang={i18n.locale}
                decimalScale={0}
              />
            </FormControl>
            <Controller
              control={form.control}
              name="numberOfSharesAfter"
              key="numberOfSharesAfter"
              rules={{ required: true, min: 0 }}
              render={({ field: { value, onChange } }) => (
                <FormControl required>
                  <FormLabel>
                    <Trans id="Number of shares after" />
                  </FormLabel>
                  <NumericFormatInput
                    sx={{ maxWidth: '200px' }}
                    value={value ?? ''}
                    onChange={onChange}
                    lang={i18n.locale}
                    decimalScale={0}
                  />
                </FormControl>
              )}
            />
            {form.watch('numberOfSharesAfter') && aoa && (
              <FormHelperText>
                <SplitLabel
                  before={aoa.numberOfSharesBefore.toString()}
                  after={form.watch('numberOfSharesAfter')!.toString()}
                />
              </FormHelperText>
            )}
            <Stack direction="row" justifyContent="flex-start">
              <Button type="submit" loading={loading}>
                <Trans id="Next" />
              </Button>
            </Stack>
          </Stack>
        </form>
      ) : (
        <Box>
          <Button onClick={stepNext}>
            <Trans id="Skip" />
          </Button>
        </Box>
      )}

      {pendingFormData && affectedData && (
        <ConfirmDialog
          header={<Trans id="One or more entities are linked to the same meeting, do you want to update them?" />}
          onConfirm={async () => {
            await onSave(pendingFormData);
            setPendingFormData(null);
          }}
          onCancel={async () => {
            await onSave({ ...pendingFormData, skipConcernUpdates: true });
            setPendingFormData(null);
          }}
          cancelLabel={<Trans id="No, I'll handle it manually" />}
          confirmProps={{ loading: loading, children: <Trans id="Yes" /> }}
        >
          <Stack gap={2}>
            {affectedData.shareIssues.length > 0 && (
              <>
                <Trans id="The following Share Issues will have their price per share, min/max number of shares and participant shares adjusted:" />
                <Box component="ul">
                  {affectedData.shareIssues.map((si, i) => (
                    <li key={i}>{si.description}</li>
                  ))}
                </Box>
              </>
            )}

            {affectedData.warrantPrograms.length > 0 && (
              <>
                <Trans id="The following Warrant Programs will have their strike price, max number of options and participant options adjusted:" />
                <Box component="ul">
                  {affectedData.warrantPrograms.map((wp, i) => (
                    <li key={i}>{wp.name || wp.description}</li>
                  ))}
                </Box>
              </>
            )}

            {affectedData.authorizationGroups.length > 0 && (
              <>
                <Trans id="The following authorizations will have their number of shares/options/convertibles adjusted:" />
                <Box component="ul">
                  {affectedData.authorizationGroups.map((ag, i) => (
                    <li key={i}>
                      {ag.validThruAgmYear || DateTime.fromISO(ag.validThruDate || '').toFormat('yyyy-MM-dd')}
                    </li>
                  ))}
                </Box>
              </>
            )}
          </Stack>
        </ConfirmDialog>
      )}
    </Stack>
  );
};
